import { chunkKeyInt, ObjectType, TILE_SIZE } from '@dreamgame/shared';
import { Container, Graphics } from 'pixi.js';
import { getAllChunks } from '../../state/chunks';
import { getAmbientLight, updateEnvironment } from '../../state/environment';
import { getCamera, getScreenDimensions } from '../Camera';
import type { VisibleChunk } from './types';

// ============================================================================
// EnvironmentLayer - Screen-space ambient light overlay (day/night cycle)
// ============================================================================
// Renders a fullscreen rect with the ambient light color/alpha.
// Sits between worldContainer and UI layers so the world is tinted
// but selection boxes and debug HUD remain unaffected.

let container: Container;
let ambientGraphics: Graphics;
let campfireLightGraphics: Graphics;
let _environmentEnabled = true;

// PERFORMANCE: Cache last drawn values to skip redundant GPU draws
let _lastColor = -1;
let _lastAlpha = -1;
let _lastWidth = -1;
let _lastHeight = -1;
let _lastCampfireLightEnabled = false;

const CAMPFIRE_LIGHT_DARKNESS_THRESHOLD = 0.08;
const CAMPFIRE_LIGHT_RADIUS_INNER = TILE_SIZE * 1.2;
const CAMPFIRE_LIGHT_RADIUS_MIDDLE = TILE_SIZE * 1.9;
const CAMPFIRE_LIGHT_RADIUS_OUTER = TILE_SIZE * 2.8;
const _chunkKeyParams = { x: 0, y: 0 };

const clamp01 = ({ value }: { value: number }): number => {
  if (value <= 0) {
    return 0;
  }
  if (value >= 1) {
    return 1;
  }
  return value;
};

export const createEnvironmentLayer = (): Container => {
  container = new Container();
  ambientGraphics = new Graphics();
  campfireLightGraphics = new Graphics();
  campfireLightGraphics.blendMode = 'add';
  container.addChild(ambientGraphics);
  container.addChild(campfireLightGraphics);
  container.visible = _environmentEnabled;
  return container;
};

export const setEnvironmentEnabled = ({ enabled }: { enabled: boolean }): void => {
  _environmentEnabled = enabled;
  if (container) {
    container.visible = enabled;
    if (!enabled) {
      ambientGraphics.clear();
      campfireLightGraphics.clear();
      _lastColor = -1;
      _lastAlpha = -1;
      _lastWidth = -1;
      _lastHeight = -1;
      _lastCampfireLightEnabled = false;
    }
  }
};

const drawCampfireLights = ({
  visibleChunks,
  darkness,
  width,
  height,
}: {
  visibleChunks: ReadonlyArray<VisibleChunk>;
  darkness: number;
  width: number;
  height: number;
}): void => {
  const loadedChunks = getAllChunks();
  const camera = getCamera();
  const zoom = camera.zoom;
  const halfWidth = width * 0.5;
  const halfHeight = height * 0.5;

  for (const { x: chunkX, y: chunkY } of visibleChunks) {
    _chunkKeyParams.x = chunkX;
    _chunkKeyParams.y = chunkY;
    const chunk = loadedChunks.get(chunkKeyInt(_chunkKeyParams));
    if (!chunk?.objects) {
      continue;
    }

    for (const obj of chunk.objects) {
      if (obj.objectType !== ObjectType.CAMPFIRE) {
        continue;
      }

      const lightStrength = darkness * 0.82;
      const innerRadius = CAMPFIRE_LIGHT_RADIUS_INNER * zoom;
      const middleRadius = CAMPFIRE_LIGHT_RADIUS_MIDDLE * zoom;
      const outerRadius = CAMPFIRE_LIGHT_RADIUS_OUTER * zoom;
      const worldX = obj.tileX * TILE_SIZE + TILE_SIZE * 0.5;
      const worldY = obj.tileY * TILE_SIZE + TILE_SIZE * 0.62;
      const screenX = halfWidth + (worldX - camera.x) * zoom;
      const screenY = halfHeight + (worldY - camera.y) * zoom;

      // Skip lights that are completely outside screen bounds.
      if (
        screenX < -outerRadius ||
        screenX > width + outerRadius ||
        screenY < -outerRadius ||
        screenY > height + outerRadius
      ) {
        continue;
      }

      campfireLightGraphics.setFillStyle({ color: 0xffe9b8, alpha: lightStrength * 0.17 });
      campfireLightGraphics.circle(screenX, screenY, innerRadius);
      campfireLightGraphics.fill();

      campfireLightGraphics.setFillStyle({ color: 0xffc675, alpha: lightStrength * 0.1 });
      campfireLightGraphics.circle(screenX, screenY, middleRadius);
      campfireLightGraphics.fill();

      campfireLightGraphics.setFillStyle({ color: 0xff8a2f, alpha: lightStrength * 0.06 });
      campfireLightGraphics.circle(screenX, screenY, outerRadius);
      campfireLightGraphics.fill();
    }
  }
};

export const drawEnvironment = ({
  visibleChunks,
}: {
  visibleChunks: ReadonlyArray<VisibleChunk>;
}): void => {
  if (!_environmentEnabled) {
    if (container) {
      container.visible = false;
    }
    return;
  }

  updateEnvironment();

  const { color, alpha } = getAmbientLight();
  const darkness = clamp01({ value: (alpha - CAMPFIRE_LIGHT_DARKNESS_THRESHOLD) / 0.7 });
  const showCampfireLights = darkness > 0.001;

  // Skip draw entirely during full daylight
  if (alpha < 0.001) {
    if (_lastAlpha >= 0.001) {
      ambientGraphics.clear();
      campfireLightGraphics.clear();
      _lastAlpha = 0;
      _lastColor = -1;
      _lastCampfireLightEnabled = false;
    }
    return;
  }

  const { width, height } = getScreenDimensions();

  const ambientChanged =
    color !== _lastColor || alpha !== _lastAlpha || width !== _lastWidth || height !== _lastHeight;

  if (ambientChanged) {
    _lastColor = color;
    _lastAlpha = alpha;
    _lastWidth = width;
    _lastHeight = height;

    ambientGraphics.clear();
    ambientGraphics.setFillStyle({ color, alpha });
    ambientGraphics.rect(0, 0, width, height);
    ambientGraphics.fill();
  }

  if (!showCampfireLights) {
    if (_lastCampfireLightEnabled) {
      campfireLightGraphics.clear();
      _lastCampfireLightEnabled = false;
    }
    return;
  }

  _lastCampfireLightEnabled = true;
  campfireLightGraphics.clear();
  drawCampfireLights({
    visibleChunks,
    darkness,
    width,
    height,
  });
};
